import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Menu, X, Phone, Sun, Moon, ChevronDown, Home,
  User, Brain, Shield, Navigation, LayoutGrid,
  Users, Heart, Briefcase, Sparkles,
  BookOpen, FolderOpen,
} from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { CalendlyBtn } from '@/components/ui/calendly-btn'
import { useTheme } from '@/context/ThemeContext'
import { phone } from '@/content/site'

type DropdownItem = { label: string; href: string; icon: React.ElementType }

const navItems: {
  label: string
  href: string | null
  children?: DropdownItem[]
}[] = [
  {
    label: 'About',
    href: '/about',
    children: [
      { label: 'About Douglas',       href: '/about',                 icon: User },
      { label: 'What is IFS?',        href: '/ifs',                   icon: Brain },
      { label: 'Coaching vs. Therapy',href: '/coaching-vs-therapy',   icon: Shield },
      { label: 'Client Journey',      href: '/about#journey',         icon: Navigation },
      { label: 'Models & Frameworks', href: '/models-frameworks', icon: LayoutGrid },
    ],
  },
  {
    label: 'Services',
    href: '/services',
    children: [
      { label: 'One-on-One Coaching',       href: '/services',                             icon: Users },
      { label: 'IFS Coaching California',   href: '/ifs-coaching-california',              icon: Heart },
      { label: 'Career Coaching California',href: '/career-coaching-california',           icon: Briefcase },
      { label: 'Self Development Coaching', href: '/self-development-coaching-california', icon: Sparkles },
    ],
  },
  {
    label: 'Explore',
    href: null,
    children: [
      { label: 'Blog',      href: '/blog',      icon: BookOpen },
      { label: 'Resources', href: '/resources', icon: FolderOpen },
    ],
  },
  { label: 'Contact', href: '/contact' },
]

function Dropdown({ items, onClose }: { items: DropdownItem[]; onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-xl overflow-hidden z-50"
    >
      {items.map((item) => (
        <Link
          key={item.href}
          to={item.href}
          onClick={onClose}
          className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
        >
          <item.icon size={13} className="text-slate-400 dark:text-slate-500 flex-shrink-0" />
          {item.label}
        </Link>
      ))}
    </motion.div>
  )
}

export default function Nav() {
  const [open, setOpen] = useState(false)
  const [dropdown, setDropdown] = useState<string | null>(null)
  const [scrolled, setScrolled] = useState(false)
  const { theme, toggle } = useTheme()
  const location = useLocation()
  const navRef = useRef<HTMLDivElement>(null)

  // All pages have dark headers and ScrollToTop ensures scroll=0 on navigation,
  // so all pages start transparent and frost on scroll.
  useEffect(() => {
    setScrolled(window.scrollY > 20)
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [location.pathname])

  useEffect(() => { setOpen(false); setDropdown(null) }, [location.pathname])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setDropdown(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const textLink = scrolled
    ? 'text-slate-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400'
    : 'text-slate-200 hover:text-blue-300'

  const navBg = scrolled
    ? 'bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-sm border-b border-slate-100 dark:border-slate-800'
    : 'bg-transparent'

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <nav ref={navRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo + Home icon */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0 select-none cursor-pointer">
            <img
              src={`${import.meta.env.BASE_URL}dp_nelson_logo.webp`}
              alt="Douglas P Nelson"
              draggable={false}
              className={`h-9 w-auto transition-all duration-300 pointer-events-none group-hover:scale-105 group-hover:brightness-110 ${scrolled ? '' : 'brightness-0 invert'}`}
            />
            <span className={`h-5 w-px mx-1 transition-colors ${scrolled ? 'bg-slate-200 dark:bg-slate-700' : 'bg-white/20'}`} />
            <Home
              size={15}
              className={`transition-colors ${scrolled ? 'text-slate-400 dark:text-slate-500 group-hover:text-blue-600' : 'text-white/50 group-hover:text-blue-300'}`}
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children ? setDropdown(item.label) : undefined}
                onMouseLeave={() => setDropdown(null)}
              >
                <div className="flex items-center">
                  {item.href ? (
                    <NavLink
                      to={item.href}
                      className={({ isActive }) =>
                        `px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : textLink
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ) : (
                    <button className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${textLink}`}>
                      {item.label}
                    </button>
                  )}
                  {item.children && (
                    <ChevronDown
                      size={13}
                      className={`-ml-1.5 mr-1 transition-all duration-200 ${dropdown === item.label ? 'rotate-180' : ''} ${textLink}`}
                    />
                  )}
                </div>
                <AnimatePresence>
                  {item.children && dropdown === item.label && (
                    <Dropdown items={item.children} onClose={() => setDropdown(null)} />
                  )}
                </AnimatePresence>
              </div>
            ))}

          </div>

          {/* Desktop right */}
          <div className="hidden md:flex items-center gap-1.5">
            <a
              href={`tel:${phone}`}
              className={`flex items-center gap-1.5 text-xs transition-colors px-2 ${
                scrolled ? 'text-slate-500 dark:text-slate-400 hover:text-blue-600' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Phone size={12} />
              <span className="hidden lg:inline">{phone}</span>
            </a>
            <button
              onClick={toggle}
              aria-label="Toggle dark mode"
              className={`p-2 rounded-lg transition-colors ${
                scrolled ? 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800' : 'text-slate-400 hover:text-white hover:bg-white/10'
              }`}
            >
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <CalendlyBtn
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full px-4 ml-1 transition-all hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25"
            >
              Book Free Consultation
            </CalendlyBtn>
          </div>

          {/* Mobile controls */}
          <div className="md:hidden flex items-center gap-1">
            <button onClick={toggle} aria-label="Toggle dark mode"
              className={`p-2 transition-colors ${scrolled ? 'text-slate-600 dark:text-slate-300' : 'text-slate-300'}`}>
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <button
              className={`p-2 transition-colors ${scrolled ? 'text-slate-700 dark:text-slate-300' : 'text-slate-300'}`}
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 shadow-lg overflow-hidden"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.href ? (
                    <Link
                      to={item.href}
                      className="block px-3 py-2.5 text-slate-700 dark:text-slate-200 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-sm font-medium transition-colors"
                      onClick={() => setOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span className="block px-3 py-2 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-2">
                      {item.label}
                    </span>
                  )}
                  {item.children && (
                    <div className="ml-4 space-y-0.5 mb-1">
                      {item.children.map((child) => (
                        <Link key={child.href} to={child.href}
                          className="flex items-center gap-2.5 px-3 py-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 text-sm rounded-lg transition-colors"
                          onClick={() => setOpen(false)}>
                          <child.icon size={13} className="text-slate-400 dark:text-slate-500 flex-shrink-0" />
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Mobile Contact CTA block — more prominent */}
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-3 mb-3">Get in Touch</p>
                <a href={`tel:${phone}`}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-2">
                  <Phone size={15} className="text-blue-500 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400 dark:text-slate-500">Call Douglas</p>
                    <p className="text-sm font-medium">{phone}</p>
                  </div>
                </a>
                <Link to="/contact" onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 mb-2 rounded-xl border border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 text-sm font-medium hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                  Send a Message
                </Link>
                <div onClick={() => setOpen(false)}>
                  <CalendlyBtn size="sm" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full w-full justify-center">
                    Book Free Consultation
                  </CalendlyBtn>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  )
}
